'use client';

import { motion } from 'framer-motion';

export function Tooth({
  index,
  isMissing = false,
}: {
  index: number;
  isMissing?: boolean;
}) {
  const sizes = [0.85, 0.92, 1, 1.05, 1.08, 1.05, 1, 0.92, 0.85];
  const scale = sizes[index] ?? 1;
  const rotations = [-3, -1.5, -0.5, 0, 0.5, 0, -0.5, -1.5, -3];
  const rotation = rotations[index] ?? 0;

  if (isMissing) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.8 + index * 0.06, duration: 0.5 }}
        className="relative flex flex-col items-center mx-[2px] sm:mx-1"
        style={{ width: `${scale * 40}px` }}
      >
        <motion.svg
          viewBox="0 0 50 72"
          fill="none"
          className="w-full h-auto opacity-20"
          initial={{ scale: 0.8 }}
          animate={{ scale: 1 }}
          transition={{ delay: 1.2, duration: 0.6, ease: 'easeOut' }}
        >
          <path
            d="M25 2C15 2 6 8 4 18C2 28 6 36 8 42C10 48 12 56 16 64C18 68 22 70 25 70C28 70 32 68 34 64C38 56 40 48 42 42C44 36 48 28 46 18C44 8 35 2 25 2Z"
            stroke="currentColor"
            strokeWidth="2"
            strokeDasharray="4 4"
            className="text-white"
          />
        </motion.svg>
        <motion.div
          className="absolute inset-0 flex items-center justify-center"
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 1.5, duration: 0.4, type: 'spring', stiffness: 200 }}
        >
          <span className="text-gold text-lg font-bold">?</span>
        </motion.div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: -30, rotate: rotation }}
      animate={{ opacity: 1, y: 0, rotate: rotation }}
      transition={{
        delay: 0.3 + index * 0.06,
        duration: 0.5,
        type: 'spring',
        stiffness: 120,
        damping: 12,
      }}
      className="mx-[2px] sm:mx-1"
      style={{ width: `${scale * 40}px` }}
    >
      <motion.svg
        viewBox="0 0 50 72"
        fill="none"
        className="w-full h-auto drop-shadow-sm"
        whileHover={{ y: -4, transition: { duration: 0.2 } }}
      >
        <path
          d="M25 2C15 2 6 8 4 18C2 28 6 36 8 42C10 48 12 56 16 64C18 68 22 70 25 70C28 70 32 68 34 64C38 56 40 48 42 42C44 36 48 28 46 18C44 8 35 2 25 2Z"
          className="fill-white"
          stroke="#E8E0D4"
          strokeWidth="1.5"
        />
        <path
          d="M25 4C17 4 9 9 7 18C9 16 15 12 25 12C35 12 41 16 43 18C41 9 33 4 25 4Z"
          className="fill-white"
          opacity="0.7"
        />
        <path
          d="M25 48C23 48 21 52 20 58C19 62 21 66 25 66C29 66 31 62 30 58C29 52 27 48 25 48Z"
          className="fill-slate-200"
          opacity="0.5"
        />
      </motion.svg>
    </motion.div>
  );
}

export function GumLine() {
  return (
    <motion.div
      initial={{ scaleX: 0 }}
      animate={{ scaleX: 1 }}
      transition={{ delay: 0.2, duration: 0.6, ease: 'easeOut' }}
      className="relative w-full max-w-md mx-auto -mt-2"
    >
      <svg viewBox="0 0 400 30" className="w-full" preserveAspectRatio="none">
        <path
          d="M0 15 Q50 0, 100 12 Q150 24, 200 12 Q250 0, 300 12 Q350 24, 400 15 L400 30 L0 30 Z"
          className="fill-pink-900/30"
        />
        <path
          d="M0 15 Q50 0, 100 12 Q150 24, 200 12 Q250 0, 300 12 Q350 24, 400 15"
          fill="none"
          className="stroke-pink-800/30"
          strokeWidth="1"
        />
      </svg>
    </motion.div>
  );
}
